<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue';
import { createReusableTemplate } from '@vueuse/core';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';

interface SearchInputProps {
	placeholder?: string;
	disabled?: boolean;
	loading?: boolean;
	/**
	 * Opens the search over the whole header instead of growing it in place, for rows too narrow to
	 * fit it. The caller's header must sit in a positioned ancestor for the overlay to cover.
	 */
	overlay?: boolean;
}

const { overlay } = defineProps<SearchInputProps>();

const [DefineSearchInput, ReuseSearchInput] = createReusableTemplate();

const searchValue = defineModel<string>({ default: '' });

const isExpanded = ref(!!searchValue.value);
// Inline, the input takes the button's place until it has finished shrinking back into it; the
// overlay covers the header instead, so its button never leaves.
const isInlineSearchShown = ref(!!searchValue.value);
const isButtonShown = computed(() => overlay || !isInlineSearchShown.value);
const rootRef = useTemplateRef('root');
const inputRef = useTemplateRef('input');

// The overlay takes the command palette's borderless search: the whole bar is the field, its icon
// starts where the title does, and the clear button sits where the header's last action was.
const inputAppearance = computed(() => overlay
	? {
			variant: 'none',
			size: 'md',
			ui: { root: 'h-full', base: 'h-full ps-6 pe-8', leading: 'ps-0', leadingIcon: 'size-4', trailing: 'pe-0' },
			clearButtonSize: 'sm',
		} as const
	: {
			variant: 'outline',
			size: 'sm',
			ui: { base: 'py-1.5', trailing: 'pe-0.5' },
			clearButtonSize: 'xs',
		} as const);
const searchButtonRef = useTemplateRef<ComponentPublicInstance>('searchButton');

// Collapsing from inside the search (Escape, clear) hands focus back to the button so keyboard
// users don't land on <body>; collapsing on focus out leaves focus wherever the user moved it.
let restoreFocusOnCollapse = false;

watch(searchValue, (newValue) => {
	if (newValue && !isExpanded.value) {
		expandSearch();
	}
}, { immediate: true });

// The header switches modes with its width, which swaps the input for a new one and cuts short any
// inline leave transition, so the inline state and focus are carried over by hand.
watch(() => overlay, () => {
	isInlineSearchShown.value = isExpanded.value;

	// A pre-flush watcher still sees the outgoing input, so focus is only moved if the search had it.
	if (!isExpanded.value || !rootRef.value?.contains(document.activeElement)) return;
	nextTick(() => inputRef.value?.inputRef?.focus());
});

// Focused within the tap's event handling rather than on a timer, which iOS won't open the keyboard for.
async function expandSearch() {
	isExpanded.value = true;
	isInlineSearchShown.value = true;
	await nextTick();
	inputRef.value?.inputRef?.focus();
}

function collapseSearch() {
	if (searchValue.value) {
		searchValue.value = '';
		return;
	}

	hideSearch({ restoreFocus: true });
}

function hideSearch({ restoreFocus }: { restoreFocus: boolean }) {
	restoreFocusOnCollapse = restoreFocus;
	isExpanded.value = false;

	// The overlay's button never left, so it can take focus back without waiting for the fade out.
	if (overlay) handleAfterLeave();
}

function clearSearch() {
	searchValue.value = '';
	inputRef.value?.inputRef?.focus();
}

// Listens on the whole expanded search, not just the input, so tabbing on from the clear button
// still collapses it; moving between the input and the clear button stays inside and is ignored.
function handleFocusOut(event: FocusEvent) {
	const expandedSearch = event.currentTarget as HTMLElement;

	if (!searchValue.value && !expandedSearch.contains(event.relatedTarget as Node | null)) {
		hideSearch({ restoreFocus: false });
	}
}

function handleAfterLeave() {
	isInlineSearchShown.value = false;

	if (!restoreFocusOnCollapse) return;
	restoreFocusOnCollapse = false;

	nextTick(() => searchButtonRef.value?.$el.focus());
}
</script>

<template>
	<div ref="root" class="flex h-6 items-center">
		<DefineSearchInput>
			<DInput
				ref="input"
				v-model="searchValue"
				class="w-full"
				:variant="inputAppearance.variant"
				:size="inputAppearance.size"
				data-testid="search-input"
				:placeholder="placeholder ?? $t('common.search')"
				:disabled
				:loading
				leading-icon="lucide:search"
				:ui="inputAppearance.ui"
				@keydown.escape="collapseSearch"
			>
				<template #trailing>
					<DButton
						data-testid="search-clear-button"
						variant="ghost"
						color="neutral"
						:size="inputAppearance.clearButtonSize"
						icon="lucide:x"
						class="rounded-sm opacity-100"
						:aria-label="$t('common.clear')"
						@click="searchValue ? clearSearch() : collapseSearch()"
					/>
				</template>
			</DInput>
		</DefineSearchInput>

		<DHeaderOverlay v-if="overlay" :open="isExpanded">
			<div class="size-full px-page" @focusout="handleFocusOut">
				<ReuseSearchInput />
			</div>
		</DHeaderOverlay>
		<Transition
			v-else
			enter-active-class="transition-[width] duration-100 ease-out [&_input]:pe-0 **:data-[slot=trailing]:transition-opacity **:data-[slot=trailing]:duration-100 **:data-[slot=leadingIcon]:transition-colors **:data-[slot=leadingIcon]:duration-100 [&_input]:placeholder:transition-opacity [&_input]:placeholder:duration-100"
			enter-from-class="w-8! **:data-[slot=trailing]:opacity-0 **:data-[slot=leadingIcon]:text-default [&_input]:placeholder:opacity-0"
			leave-active-class="transition-[width] duration-100 ease-out [&_input]:pe-0 **:data-[slot=trailing]:transition-opacity **:data-[slot=trailing]:duration-100 **:data-[slot=leadingIcon]:transition-colors **:data-[slot=leadingIcon]:duration-100 [&_input]:placeholder:transition-opacity [&_input]:placeholder:duration-100"
			leave-to-class="w-8! **:data-[slot=trailing]:opacity-0 **:data-[slot=leadingIcon]:text-default [&_input]:placeholder:opacity-0"
			@after-leave="handleAfterLeave"
		>
			<div
				v-if="isExpanded"
				class="w-56 overflow-hidden"
				@focusout="handleFocusOut"
			>
				<ReuseSearchInput />
			</div>
		</Transition>
		<DTooltip v-if="isButtonShown" :text="disabled ? $t('common.search_unavailable') : $t('common.search')">
			<DButton
				ref="searchButton"
				data-testid="search-button"
				variant="outline"
				size="sm"
				color="neutral"
				icon="lucide:search"
				:aria-label="$t('common.search')"
				:disabled
				@click="expandSearch"
			/>
		</DTooltip>
	</div>
</template>
